import {
    AfterViewInit,
    Component,
    NgZone,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { NgForm, Validators } from '@angular/forms';
import { NgbActiveModal } from '@cmps/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { AuthService } from '../auth/auth.service';
import { AbstractCmpsForm, CmpsFormFields } from '../common/common.form';
import { SecurityQuestion, User } from '../user/user.model';
import { UserService } from '../user/user.service';

import { TranslationKeyMap } from './profile.i18n';

const SecurityQuestionFormFields: CmpsFormFields = {
    SECURITY_QUESTION: {
        id: 'securityQuestion',
        name: 'securityQuestion',
        label: TranslationKeyMap._706_SECURITY_QUESTION,
        placeholder: TranslationKeyMap._706_SECURITY_QUESTION,
        validators: [Validators.required, Validators.minLength(5)],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            minlength:
                TranslationKeyMap._838_SECURITY_QUESTION_MUST_BE_A_MINIMUM_OF__REQUIREDLENGTH__CHARACTERS_,
        },
    },
    ANSWER: {
        id: 'answer',
        name: 'answer',
        label: TranslationKeyMap._707_ANSWER,
        placeholder: TranslationKeyMap._707_ANSWER,
        validators: [Validators.required, Validators.minLength(5)],
        validationMsgs: {
            required:
                TranslationKeyMap._640__FIELD_LABEL__IS_A_REQUIRED_FIELD__PLEASE_ENTER_A__FIELD_LABEL_,
            minlength:
                TranslationKeyMap._839_ANSWER_MUST_BE_A_MINIMUM_OF__REQUIREDLENGTH__CHARACTERS_,
        },
    },
};

class SecurityQuestionForm extends AbstractCmpsForm {
    constructor(protected _translateService: TranslateService) {
        super(SecurityQuestionFormFields, _translateService);
    }

    get name(): string {
        return 'securityQuestionForm';
    }
}

@Component({
    selector: 'cmps-profile-security-question',
    templateUrl: './security-question.profile.component.html',
})
export class SecurityQuestionProfileComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @ViewChild('securityQuestionNgForm')
    securityQuestionNgForm: NgForm;

    i18n = TranslationKeyMap;
    hasPendingData: boolean;
    user: User;
    model: SecurityQuestion;
    form: SecurityQuestionForm;
    private _retrieveSub: Subscription;
    private _ngZoneSub: Subscription;
    private _updateSub: Subscription;
    success: boolean;
    failure: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        public activeModal: NgbActiveModal,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _userService: UserService,
    ) {
        this.form = new SecurityQuestionForm(_translateService);
    }

    ngOnInit(): void {
        this.hasPendingData = true;
        this._retrieveSub = this._userService
            .retrieveSecurityQuestion(this.user.securityInfo.username)
            .subscribe(
                (securityQuestion) => {
                    this.model = securityQuestion;
                    this.model.userId = this._authService.user.id;
                },
                // eslint-disable-next-line n/handle-callback-err
                (error) => {
                    this.failure = true;
                    this.hasPendingData = false;
                },
                // eslint-disable-next-line no-return-assign
                () => (this.hasPendingData = false),
            );
    }

    ngAfterViewInit(): void {
        this._ngZoneSub = this._ngZone.onStable.subscribe(() => {
            if (this.hasPendingData) return;
            if (this.securityQuestionNgForm) {
                this.form.form = this.securityQuestionNgForm;
                try {
                    this.form.bind();
                } catch (err) {
                    console.error(err);
                }
            }
            this._ngZoneSub.unsubscribe();
        });
    }

    ngOnDestroy(): void {
        if (this._updateSub) {
            this._updateSub.unsubscribe();
        }
        if (this._ngZoneSub) {
            this._ngZoneSub.unsubscribe();
        }
        if (this._retrieveSub) {
            this._retrieveSub.unsubscribe();
        }
    }

    onSubmit(): void {
        this.form.validate();
        if (!this.form.isValid()) return;

        this.failure = false;
        this.form.submitted = true;
        this._updateSub = this._userService
            .updateSecurityQuestion(this.model)
            // eslint-disable-next-line no-return-assign
            .subscribe(
                () => (this.success = true),
                (error) => {
                    this.failure = true;
                    this.form.serverError =
                        error.message !== 'null' ? error.message : null;
                    this.form.submitted = false;
                },
                // eslint-disable-next-line no-return-assign
                () => (this.form.submitted = false),
            );
    }
}
